import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useFeatureFlag } from 'featureFlags';
import { useOrientation } from 'hooks/useOrientation';
import { saloonFonts, saloonTokens } from 'styles/saloonTheme';

import { ActionVariantKey, actionVariants, variantFromLabel } from './actionVariants';

type CheckboxActionType = {
  label?: string;
  // A second, smaller line under the label (EN-11023: the straddle's amount
  // under "Straddle next hand"). The checkbox stays centred against both lines.
  sublabel?: React.ReactNode;
  value?: string;
  checked?: boolean;
  // Returning `false` refuses the click: the chip keeps its current state (a
  // caller that could not send the change, or is still waiting on the last one).
  onChange?: (value: string) => void | boolean;
  disabled?: boolean;
  // Radio-style: clicking the already-selected option does nothing (can't
  // deselect to "none"). Used by the Post Blind / Wait For Big Blind pair where
  // exactly one must always be chosen. Pre-actions leave this off (togglable).
  noDeselect?: boolean;
  // Neutral-until-armed colouring. When set, an UNCHECKED button renders in a
  // muted grey and only lights up in its action colour (red/blue/green/orange)
  // once selected — so the mid-hand pre-action row reads as "nothing armed" at
  // rest and can't be mistaken for the always-coloured live action row. The join
  // pair (Post Blind / Wait for Big Blind) leaves this off and stays colour-coded.
  neutralUntilSelected?: boolean;
  // Force the SELECTED (coloured) variant instead of deriving it from the label.
  // The join pair uses this with 'raise' so whichever of Post Blind / Wait for Big
  // Blind is chosen lights up in the same gold/orange (the label-derived colour
  // would make Wait for Big Blind blue). Pre-actions omit it and stay per-action.
  activeVariant?: ActionVariantKey;
  // Portrait: take half the row, up to this width and never narrower than the
  // label itself (with normal side padding), instead of stretching like a
  // pre-action. EN-11023: the straddle chip, sized like Show/Muck Cards
  // (ActionButton's maxWidth), so beside them the row splits 50/50 and alone it
  // sits centred rather than spanning the bar.
  maxWidth?: string;
  // Draw the checkbox even while `release.preaction-checkbox-indicator` is off.
  // EN-11023: the straddle chip is new (no colour-only look to keep), commits
  // 2x BB at the deal, and reads the same on and off, so colour alone is not enough.
  alwaysShowCheckbox?: boolean;
};

const CheckboxAction = ({
  label,
  sublabel,
  value = '',
  checked,
  onChange,
  disabled = false,
  noDeselect = false,
  neutralUntilSelected = false,
  activeVariant,
  maxWidth,
  alwaysShowCheckbox = false
}: CheckboxActionType) => {
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const checkboxFlag = useFeatureFlag('preactionCheckboxIndicator');
  const showCheckbox = checkboxFlag || alwaysShowCheckbox;
  const [internalChecked, setInternalChecked] = useState(checked);
  const [internalDisabled, setInternalDisabled] = useState(disabled);

  useEffect(() => {
    setInternalChecked(checked);
  }, [checked]);

  useEffect(() => {
    setInternalDisabled(disabled);
  }, [disabled]);

  const handleToggle = useCallback(() => {
    // Radio mode: clicking the selected option is a no-op (the other option is
    // deselected via its `checked` prop when this one is picked).
    if (noDeselect) {
      if (!internalChecked && onChange && onChange(value) !== false) {
        setInternalChecked(true);
      }
      return;
    }
    const finalChecked = !internalChecked;
    if (onChange && onChange(finalChecked ? value : '') !== false) {
      setInternalChecked(!internalChecked);
    }
  }, [internalChecked, onChange, value, noDeselect]);

  // Colour-code the chip by its label so the pre-action row matches the action
  // row: Check/Fold → red, Check → blue, Call/Call Any → green, All In → orange
  // (EN-10265). Shares the exact palette + dashed-stitch treatment with
  // ActionButton via the actionVariants module.
  const v = actionVariants[activeVariant ?? variantFromLabel(label)];

  // Neutral (unarmed) palette — used only when neutralUntilSelected is on and the
  // button isn't checked. Muted grey leather so the action colour reads as a
  // deliberate "armed" state, not the resting look.
  const neutral = {
    bg: 'linear-gradient(180deg, #2b3039, #171a21)',
    border: 'rgba(255,255,255,0.22)',
    dash: 'rgba(241,227,199,0.32)',
    text: 'rgba(241,227,199,0.82)',
    shadow: '0 2px 0 rgba(0,0,0,0.6)'
  };
  const eff = neutralUntilSelected && !internalChecked ? neutral : v;

  // Sizes match the ORIGINAL pre-action chip (0.72rem, 6px radius, 0.04em) —
  // only the STYLE changes here (label-driven colour, no icon, bold weight).
  // The dashed "stitch" is drawn as an inset overlay span (see below), NOT as
  // the button's own `border: dashed` — a dashed border on a rounded button
  // renders its dashes unevenly around the corners (the "off" border). The
  // solid outer border + straight inset overlay render cleanly, and this
  // matches how ActionButton draws its stitch exactly.
  const style: CSSProperties = {
    position: 'relative',
    overflow: 'visible',
    background: eff.bg,
    border: `1px solid ${eff.border}`,
    borderRadius: '6px',
    color: eff.text,
    // Portrait: chips stretch to share the row (flex 1, tight padding), unless
    // maxWidth sizes one between its own label and that cap (see the prop).
    ...(isPortrait && maxWidth
      ? {
          padding: '0 1rem',
          flex: '0 1 50%',
          minWidth: 'max-content',
          maxWidth
        }
      : {
          padding: isPortrait ? '0 0.4rem' : '0.5rem 1rem',
          flex: isPortrait ? 1 : undefined
        }),
    fontFamily: saloonFonts.numeric, // 'Oswald' — handles amounts/numerals cleanly
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    textShadow: eff.shadow,
    whiteSpace: 'nowrap',
    transition: 'transform 0.12s ease',
    boxShadow: '0 3px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,180,92,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: internalDisabled ? 'default' : 'pointer',
    // Match the 44px history/chat icon buttons so the pre-action / post-blind
    // row's top aligns with them and clears the player info card above it.
    minHeight: '44px',
    opacity: internalDisabled ? 0.4 : 1,
    // Selected: brighten + accent ring so the chosen pre-action reads as clearly
    // "armed" against the unselected siblings.
    ...(internalChecked
      ? {
          boxShadow: `0 0 0 1px ${v.border}, 0 3px 8px rgba(0,0,0,0.5), inset 0 0 14px rgba(255,255,255,0.14)`,
          filter: 'brightness(1.18)'
        }
      : {})
  };

  return (
    <button
      onClick={internalDisabled ? undefined : handleToggle}
      style={style}
      disabled={internalDisabled}
      aria-pressed={!!internalChecked}>
      {/* Inset dashed "stitch" edge — a positioned overlay (not a dashed
          border) so the dashes stay even around the rounded corners. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: '3px',
          border: `1px dashed ${eff.dash}`,
          borderRadius: '4px',
          pointerEvents: 'none'
        }}
      />
      {showCheckbox ? <PreActionCheckbox checked={!!internalChecked} color={eff.text} /> : null}
      {sublabel === undefined ? (
        label
      ) : (
        <span style={twoLineLabelStyle}>
          <span>{label}</span>
          <span style={sublabelStyle}>{sublabel}</span>
        </span>
      )}
    </button>
  );
};

// Label over sublabel, centred; the button's own row keeps the checkbox centred
// against the pair.
const twoLineLabelStyle: CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  lineHeight: 1.1
};

const sublabelStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: '0.66rem',
  letterSpacing: '0.02em',
  fontVariantNumeric: 'tabular-nums'
};

// The explicit "selected" mark on every chip — pre-actions and the Post Blind /
// Wait for Big Blind pair alike — like the old Unity client's checkmark
// (EN-10912). Colour alone was the only cue, and an armed chip takes the same
// colour as a live action button — so a Call armed by a click meant for the
// previous turn read as "it's my turn", not "Call is queued". Behind
// `release.preaction-checkbox-indicator`; off is the colour-only look.
const PreActionCheckbox = ({ checked, color }: { checked: boolean; color: string }) => {
  // The tick is cut out of the filled box, so it takes the opposite of the
  // label colour: dark on bone-labelled chips, bone on the dark-ink orange ones.
  const tickColor = color === saloonTokens.ink ? saloonTokens.bone : saloonTokens.ink;
  return (
    <span
      aria-hidden
      data-testid='preaction-checkbox'
      data-checked={checked}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: '13px',
        height: '13px',
        marginRight: '6px',
        border: `1.5px solid ${color}`,
        borderRadius: '3px',
        background: checked ? color : 'transparent'
      }}>
      {checked ? (
        <svg width='9' height='9' viewBox='0 0 12 12'>
          <path
            d='M2 6.5 5 9.5 10 2.5'
            fill='none'
            stroke={tickColor}
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      ) : null}
    </span>
  );
};

export default CheckboxAction;
